<template>
    <div class="head">
        <div class="title">{{ trans.paymentMethodsReport.paymentMethodsReport }}</div>
        <GoHomeButton @click.prevent="onGoHomeButtonClick" />
        <UserMenuComponent />
    </div>
    <div class="payment-methods-report-content">
        <ScrollableView style="height: 100%">
            <div class="filter-section">
                <div v-html="dateRangeFilterHeader" />
                <div v-html="userFilterHeader" />
            </div>
            <DataTable :value="tableData" showGridlines>
                <Column field="paymentMethod" :header="trans.paymentMethodsReport.paymentMethod"></Column>
                <Column field="quantityNet" :header="trans.paymentMethodsReport.netQuantity"></Column>
                <Column field="grossNetSales" :header="trans.paymentMethodsReport.grossSalesNetChfInclTip"></Column>
                <Column field="tipNet" :header="trans.paymentMethodsReport.tipNet"></Column>
            </DataTable>
        </ScrollableView>
    </div>
</template>

<script setup lang="ts">
    import { TastierClient } from "@/app/TastierClient";
    import { PaymentMethodReportController } from "@/app/controller/PaymentMethodReportController";
    import { appEvents } from "@/app/events/appEvents";
    import { trans } from "@/app/localization/Trans";
    import type { IPaymentMethodReportRow } from "@/data/model/PaymentMethodsReportData";
    import { usePaymentStore } from "@/data/stores/payment";
    import { DateTime } from "@/data/util/DateTime";
    import { replaceString } from "@/data/util/String";
    import { translations } from "@/shared/localization/translations";
    import { toMoney } from "@/shared/utils/format";
    import { nextTick, onMounted, ref } from "vue";

    import GoHomeButton from "@/app/components/header/GoHomeButton.vue";
    import UserMenuComponent from "@/app/components/header/UserMenuComponent.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import Column from "primevue/column";
    import DataTable from "primevue/datatable";

    const dateRangeFilterHeader = ref<string>("");
    const userFilterHeader = ref<string>("");
    const tableData = ref<any[] | null>([]);
    const areEventsInitialized = ref(false);
    const isViewVisible = ref(false);

    const payment = usePaymentStore();

    PaymentMethodReportController.onShown.add(onBecomeVisible);
    PaymentMethodReportController.onHidden.add(onBecomeInvisible);
    PaymentMethodReportController.onUpdateData.add(refreshTableData);

    class TableRow {
        paymentMethod: string;
        quantityNet: string;
        grossNetSales: string;
        tipNet: string;

        constructor(paymentMethod: string, netQuantity: string, grossNetSales: string, tipNet: string) {
            this.paymentMethod = paymentMethod;
            this.quantityNet = netQuantity;
            this.grossNetSales = grossNetSales;
            this.tipNet = tipNet;
        }
    }

    // define functions
    async function refreshTableData() {
        const now = new Date();
        let lastPaymentMethodsData = payment.lastPaymentMethodsData;

        if (lastPaymentMethodsData) {
            dateRangeFilterHeader.value = replaceString(
                trans.paymentMethodsReport.dateRangeGenerated,
                DateTime.toPreciseDateTimeString(now),
                DateTime.toPreciseDateTimeString(lastPaymentMethodsData.filters.startDate),
                DateTime.toPreciseDateTimeString(lastPaymentMethodsData.filters.endDate)
            );

            const userName = lastPaymentMethodsData.filters?.userName ?? "";
            if (userName.length > 0) {
                userFilterHeader.value = `${translations.cancellation.user}: <b>${userName}</b>`;
            }

            tableData.value = convertPaymentMethodsToRows(lastPaymentMethodsData?.data ?? []);
            tableData.value.push(convertTotalToRow(lastPaymentMethodsData?.totals!));
        } else {
            dateRangeFilterHeader.value = "";
            userFilterHeader.value = "";
            tableData.value = [];
        }
    }

    async function onBecomeVisible(this: any) {
        isViewVisible.value = true;
        await nextTick();

        if (TastierClient.isInitialized) {
            await refreshTableData();
        } else {
            appEvents.tastierClient.initialized.addOnce(refreshTableData);
        }

        if (!areEventsInitialized.value) {
            areEventsInitialized.value = true;
        }
    }

    function onBecomeInvisible(this: any) {
        isViewVisible.value = false;

        if (areEventsInitialized.value) {
            areEventsInitialized.value = false;
        }
    }

    function convertPaymentMethodsToRows(paymentMethod: IPaymentMethodReportRow[]): TableRow[] {
        return paymentMethod.map((c) => {
            return new TableRow(
                c.paymentMethodName,
                c.quantityNet.toString(),
                toMoney(c.grossSalesInclTips, true),
                toMoney(c.tipNet, true)
            );
        });
    }

    function convertTotalToRow(paymentMethod: IPaymentMethodReportRow): TableRow {
        return new TableRow(
            trans.receipt.total,
            paymentMethod.quantityNet.toString(),
            toMoney(paymentMethod.grossSalesInclTips, true),
            toMoney(paymentMethod.tipNet, true)
        );
    }

    function onGoHomeButtonClick() {
        PaymentMethodReportController.onShowServices.dispatch();
    }

    onMounted(() => {
        onBecomeVisible();
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .view.payment-methods-report {
        display: flex;
        flex-direction: column;

        > .head {
            flex: 0 0 auto;
        }

        > .payment-methods-report-content {
            height: 100%;

            .filter-section {
                display: flex;
                flex-direction: column;
                row-gap: 0.3rem;
                font-weight: 400;
                padding: 1rem;
                font-size: 14px;
            }
        }

        table.p-datatable-table {
            font-size: 14px;
        }
    }
</style>
